import React, { useRef } from 'react';
import { Box, Container, Typography, Fab, Chip } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import { AsyncDialog, AsyncDialogRef } from '../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../../components/ConfirmationDialog';
import { Table, TableRef } from '../../../components/Table/Table';
import { useAppDispatch } from '../../../redux/hooks';
import { locations } from '../../../routes/locations';
import {
  fetchCompanyDepartments,
  deleteCompanyDepartment,
  restoreCompanyDepartment,
} from '../../../redux/modules/b2bThriftPlan/departments/companyDepartments.actions';
import {
  companyDepartmentsLoadingSelector,
  companyDepartmentsSelector,
  companyDepartmentRestoreLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/departments/companyDepartments.selectors';

export const CompanyDepartments = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyDepartments, totalCompanyDepartments } = useSelector(companyDepartmentsSelector);
  const isLoading = useSelector(companyDepartmentsLoadingSelector);
  const isRestoreLoading = useSelector(companyDepartmentRestoreLoadingSelector);
  const tableRef = useRef<TableRef>(null);
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { enqueueSnackbar } = useSnackbar();

  const onAddNew = (): void => {
    if (companyId) {
      navigate(locations.companyDepartment(Number(companyId), 'create'));
    }
  };

  const onEditDepartment = (departmentId: number): void => {
    if (companyId) {
      navigate(locations.companyDepartment(Number(companyId), departmentId.toString()));
    }
  };

  const onRestoreDepartment = async (departmentId: number): Promise<void> => {
    if (!companyId) return;

    try {
      await dispatch(restoreCompanyDepartment({ companyId: Number(companyId), departmentId })).unwrap();
      enqueueSnackbar('Company department restored successfully!', { variant: 'success' });
      tableRef.current?.reload();
    } catch (err: any) {
      enqueueSnackbar(`Restore failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  const onDeleteDepartment = async (departmentId: number): Promise<void> => {
    const isConfirmed = await asyncDialogRef.current?.show({
      title: 'Delete Company Department',
      description: 'Are you sure you want to delete this company department?',
      onConfirm: async () => {
        if (companyId) {
          await dispatch(deleteCompanyDepartment({ companyId: Number(companyId), departmentId })).unwrap();
          enqueueSnackbar('Company department deleted successfully!', { variant: 'success' });
          tableRef.current?.reload();
        }
      },
    });

    // Error handling is done in the dialog's onConfirm callback
    if (!isConfirmed && companyId) {
      // User cancelled, no action needed
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      width: 100,
      getActions: (params: GridRowParams) => {
        const { isDeleted } = params.row;
        return [
          <GridActionsCellItem
            key={`edit-company-department-${params.row.id}`}
            icon={<EditOutlinedIcon />}
            label="Edit"
            onClick={() => onEditDepartment(params.row.id)}
            disabled={isDeleted}
          />,
          <GridActionsCellItem
            key={`delete-company-department-${params.row.id}`}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => onDeleteDepartment(params.row.id)}
            disabled={isDeleted}
          />,
          <GridActionsCellItem
            key={`restore-company-department-${params.row.id}`}
            icon={<RestoreIcon />}
            label="Restore"
            onClick={() => onRestoreDepartment(params.row.id)}
            disabled={!isDeleted || isRestoreLoading}
          />,
        ];
      },
    },
    {
      field: 'id',
      headerName: 'Id',
      width: 70,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 400,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'isDeleted',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const { isDeleted } = params.row;
        return (
          <Chip
            label={isDeleted ? 'Deleted' : 'Active'}
            sx={{
              backgroundColor: isDeleted ? '#ff4444' : '#4caf50',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
  ];
  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Company Departments &nbsp;
          <Fab color="primary" size="small" aria-label="Create Department" onClick={onAddNew} sx={{ mr: 1 }}>
            <AddIcon />
          </Fab>
        </Typography>
      </Box>

      <Table
        ref={tableRef}
        columns={columns}
        rows={companyDepartments}
        rowCount={totalCompanyDepartments}
        fetchItems={fetchCompanyDepartments}
        fetchItemsCustomParams={{ companyId: Number(companyId) }}
        loading={isLoading}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        toolbarExportProps={{
          csvOptions: { fileName: `company-department-${Date.now()}` },
          excelOptions: { fileName: `company-department-${Date.now()}` },
        }}
      />

      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </Container>
  );
};
